// ============================================================
// IoTService.jsx
//
// Project showcase section displaying real-world production
// projects built and deployed by platform students.
//
// Layout:
//   - Hero headline + tagline badge
//   - Two project categories with image cards
//   - Each card shows project name with overlay on hover
// ============================================================

import React from "react";
import { styled } from "@mui/material/styles";
import {
    Typography, Box, Container, Grid, Chip,
} from "@mui/material";
import { Fade } from "react-awesome-reveal";

// ── MUI Icons (replacing react-icons) ────────────────────────
import BrushIcon from "@mui/icons-material/Brush";
import CodeIcon from "@mui/icons-material/Code";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import BusinessIcon from "@mui/icons-material/Business";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";

// ── Asset Imports ─────────────────────────────────────────────
import feetImgs from "../assets/images/feetImgs.png";
import carImg from "../assets/images/carImg.png";
import carHireImg from "../assets/images/carHireImg.png";
import EdTechImg from "../assets/images/EdTechImg.png";
import EduTechImg from "../assets/images/EduTechImg.png";
import EcommerceImg from "../assets/images/EcommerceImg.png";

// ============================================================
//  CONSTANTS
// ============================================================

/**
 * Brand colors — consistent with the rest of the platform.
 */
const BRAND = {
    dark:    "#100720",
    gold:    "#E5C95F",
    cream:   "#F6E7BC",
    white:   "#FFFFFF",
};

/**
 * TAGLINE_ITEMS
 * Three pillars shown in the hero badge.
 */
const TAGLINE_ITEMS = [
    { icon: <BrushIcon sx={{ fontSize: 18 }} />,        label: "Industry Standard" },
    { icon: <CodeIcon sx={{ fontSize: 18 }} />,         label: "Web & Mobile Apps" },
    { icon: <RocketLaunchIcon sx={{ fontSize: 18 }} />, label: "Enterprise Solutions" },
];

/**
 * PROJECT_CATEGORIES
 * Two groups of real-world projects shown as image cards.
 * Clean Code: data is kept separate from UI logic.
 *
 * To add a new project, simply add an object to the
 * relevant category's `projects` array.
 */
const PROJECT_CATEGORIES = [
    {
        category: "Mobility & Delivery",
        icon: <DirectionsCarIcon fontSize="small" />,
        projects: [
            { name: "Fleet Management App",  bg: feetImgs   },
            { name: "Car Hiring App",        bg: carImg     },
            { name: "Product Delivery App",  bg: carHireImg },
        ],
    },
    {
        category: "Business & Enterprise",
        icon: <BusinessIcon fontSize="small" />,
        projects: [
            { name: "EdTech Web App",       bg: EdTechImg    },
            { name: "E-Commerce App",       bg: EcommerceImg },
            { name: "Human Resource App",   bg: EduTechImg   },
        ],
    },
];

// ============================================================
//  STYLED COMPONENTS
// ============================================================

/**
 * SectionWrapper
 * Full-width cream background section container.
 */
const SectionWrapper = styled("section")({
    width: "100%",
    padding: "60px 0",
    fontFamily: "'Poppins', sans-serif",
    textAlign: "center",
    backgroundColor: "inherit",
});

/**
 * CategoryLabel
 * Left-aligned section heading with a dark left border accent.
 * Gives a professional editorial feel to each group.
 */
const CategoryLabel = styled(Box)({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontWeight: 700,
    fontSize: "1.2rem",
    marginTop: "48px",
    marginBottom: "24px",
    color: BRAND.dark,
    textAlign: "left",
    borderLeft: `5px solid ${BRAND.dark}`,
    paddingLeft: "16px",
});

/**
 * TaglineBadge
 * Dark pill badge showing the three platform pillars.
 */
const TaglineBadge = styled(Box)({
    display: "inline-flex",
    alignItems: "center",
    gap: "16px",
    backgroundColor: BRAND.dark,
    color: BRAND.white,
    padding: "12px 28px",
    borderRadius: "100px",
    fontSize: "0.9rem",
    fontWeight: 500,
    letterSpacing: "0.5px",
    boxShadow: "0px 4px 20px rgba(0,0,0,0.12)",
    flexWrap: "wrap",
    justifyContent: "center",
});

/**
 * BadgeDivider
 * Subtle vertical separator between tagline items.
 */
const BadgeDivider = styled(Box)({
    width: "1px",
    height: "20px",
    backgroundColor: "rgba(255,255,255,0.2)",
});

/**
 * CardBox
 * Image card container with overlay effect.
 * Darkens and scales the text label on hover.
 */
const CardBox = styled(Box)({
    position: "relative",
    width: "100%",
    height: "280px",
    borderRadius: "16px",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
    "&:hover": {
        transform: "scale(1.02)",
        boxShadow: "0 15px 45px rgba(0,0,0,0.18)",
        "& .card-overlay": {
            background: "rgba(16,7,32,0.65)",
        },
        "& .card-text": {
            transform: "scale(1.08)",
        },
    },
});

/**
 * ImageBackground
 * Full-cover project image behind the overlay.
 */
const ImageBackground = styled("img")({
    position: "absolute",
    width: "100%",
    height: "100%",
    objectFit: "cover",
    zIndex: 1,
});

/**
 * Overlay
 * Semi-transparent dark tint over the image for
 * text readability. Darkens further on hover.
 */
const Overlay = styled("div")({
    position: "absolute",
    inset: 0,
    background: "rgba(16,7,32,0.4)",
    zIndex: 2,
    transition: "background 0.3s ease",
});

/**
 * ProjectName
 * Glassmorphism label shown over the project image.
 */
const ProjectName = styled("span")({
    position: "relative",
    zIndex: 3,
    color: BRAND.white,
    fontWeight: 700,
    fontSize: "1rem",
    textAlign: "center",
    padding: "10px 20px",
    textTransform: "uppercase",
    letterSpacing: "1px",
    transition: "transform 0.4s ease",
    backgroundColor: "rgba(255,255,255,0.1)",
    backdropFilter: "blur(4px)",
    borderRadius: "8px",
    margin: "0 15px",
    border: "1px solid rgba(255,255,255,0.15)",
});

// ============================================================
//  MAIN COMPONENT
// ============================================================

export default function IoTService() {
    return (
        <SectionWrapper>
            <Container maxWidth="lg">

                {/* ── Hero: Section Label + Headline + Badge ── */}
                <Fade direction="down" triggerOnce>

                    {/* Section overline */}
                    <Typography
                        variant="overline"
                        sx={{
                            fontSize: { xs: "0.75rem", md: "0.85rem" },
                            fontWeight: 800,
                            color: BRAND.dark,
                            letterSpacing: "3px",
                            display: "block",
                            mb: 1,
                        }}
                    >
                        PROJECT SHOWCASE
                    </Typography>

                    {/* Hero headline */}
                    <Typography
                        variant="h3"
                        sx={{
                            fontSize: { xs: "1.8rem", md: "2.5rem" },
                            fontWeight: 900,
                            color: BRAND.dark,
                            fontFamily: "'Georgia', serif",
                            mb: 3,
                            lineHeight: 1.2,
                        }}
                    >
                        Real Projects.{" "}
                        <Box component="span" sx={{ color: "#b8860b" }}>
                            Real Deployment.
                        </Box>
                    </Typography>

                    {/* Tagline badge */}
                    <TaglineBadge>
                        {TAGLINE_ITEMS.map((item, index) => (
                            <React.Fragment key={item.label}>
                                {index > 0 && <BadgeDivider />}
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Box sx={{ color: BRAND.gold }}>{item.icon}</Box>
                                    <Typography variant="body2" fontWeight={600}>
                                        {item.label}
                                    </Typography>
                                </Box>
                            </React.Fragment>
                        ))}
                    </TaglineBadge>

                </Fade>

                {/* ── Project Category Sections ── */}
                {PROJECT_CATEGORIES.map((group, groupIndex) => (
                    <Fade
                        key={group.category}
                        direction="up"
                        delay={groupIndex * 150}
                        triggerOnce
                    >
                        <Box sx={{ mb: 6 }}>

                            {/* Category heading with icon */}
                            <CategoryLabel>
                                <Box sx={{ color: BRAND.dark, display: "flex" }}>
                                    {group.icon}
                                </Box>
                                <Typography
                                    variant="h6"
                                    fontWeight={800}
                                    color={BRAND.dark}
                                    sx={{ fontFamily: "'Georgia', serif" }}
                                >
                                    {group.category}
                                </Typography>
                            </CategoryLabel>

                            {/* Project Cards Grid */}
                            <Grid container spacing={3}>
                                {group.projects.map((project) => (
                                    <Grid item xs={12} sm={6} md={4} key={project.name}>
                                        <CardBox>
                                            {/* Background image */}
                                            <ImageBackground
                                                src={project.bg}
                                                alt={project.name}
                                            />
                                            {/* Dark overlay */}
                                            <Overlay className="card-overlay" />
                                            {/* Project name label */}
                                            <ProjectName className="card-text">
                                                {project.name}
                                            </ProjectName>
                                        </CardBox>
                                    </Grid>
                                ))}
                            </Grid>

                        </Box>
                    </Fade>
                ))}

                {/* ── Bottom Summary Chips ── */}
                <Fade direction="up" delay={400} triggerOnce>
                    <Box
                        display="flex"
                        justifyContent="center"
                        flexWrap="wrap"
                        gap={1.5}
                        mt={2}
                    >
                        {[
                            "Production Ready",
                            "Live Deployment",
                            "Real Client Projects",
                            "Premium Plan Exclusive",
                        ].map((label) => (
                            <Chip
                                key={label}
                                label={label}
                                sx={{
                                    backgroundColor: BRAND.dark,
                                    color: BRAND.gold,
                                    fontWeight: 600,
                                    fontSize: "0.78rem",
                                }}
                            />
                        ))}
                    </Box>
                </Fade>

            </Container>
        </SectionWrapper>
    );
}
